class ButcherBlockPurchase extends HTMLElement {
  constructor() {
    super();

    this.minimumQuantity = Number(this.dataset.minimumQuantity || 4);
    this.quantityInputs = Array.from(this.querySelectorAll('[data-bbp-quantity]'));
    this.submitButton = this.querySelector('[data-bbp-submit]');
    this.underMinimumQuoteButton = this.querySelector('[data-bbp-under-minimum-quote]');
    this.status = this.querySelector('[data-bbp-status]');
    this.totalOutput = this.querySelector('[data-bbp-total]');
    this.mobileSelect = this.querySelector('[data-bbp-mobile-select]');
    this.mobileQuantity = this.querySelector('[data-bbp-mobile-quantity]');
    this.mobileAddButton = this.querySelector('[data-bbp-mobile-add]');
    this.mobileList = this.querySelector('[data-bbp-mobile-list]');
    this.mobileEmpty = this.querySelector('[data-bbp-mobile-empty]');
  }

  connectedCallback() {
    this.quantityInputs.forEach((input) => {
      input.addEventListener('input', this.updateState.bind(this));
      input.addEventListener('change', this.updateState.bind(this));
    });

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.addSelectedItems.bind(this));
    }

    if (this.mobileAddButton) {
      this.mobileAddButton.addEventListener('click', this.updateMobileSelection.bind(this));
    }

    if (this.mobileList) {
      this.mobileList.addEventListener('click', this.removeMobileSelection.bind(this));
    }

    this.updateState();
  }

  findQuantityInput(variantId) {
    return this.quantityInputs.find((input) => input.dataset.variantId === String(variantId));
  }

  getSelectedItems() {
    return this.quantityInputs
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Math.max(0, Number(input.value || 0)),
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  getTotalQuantity() {
    return this.getSelectedItems().reduce((sum, item) => sum + item.quantity, 0);
  }

  updateState() {
    const totalQuantity = this.getTotalQuantity();
    const hasMinimum = totalQuantity >= this.minimumQuantity;

    if (this.totalOutput) {
      this.totalOutput.textContent = totalQuantity;
    }

    if (this.submitButton) {
      this.submitButton.disabled = !hasMinimum;
      this.submitButton.setAttribute('aria-disabled', String(!hasMinimum));
    }

    if (this.underMinimumQuoteButton) {
      this.underMinimumQuoteButton.hidden = totalQuantity === 0 || hasMinimum;
    }

    this.setStatus(
      hasMinimum
        ? this.dataset.readyMessage
        : this.dataset.minimumMessage.replace('[count]', this.minimumQuantity),
      hasMinimum ? 'ready' : 'warning'
    );

    this.renderMobileSummary();
  }

  updateMobileSelection() {
    if (!this.mobileSelect || !this.mobileQuantity) return;

    const variantId = this.mobileSelect.value;
    const quantityInput = this.findQuantityInput(variantId);
    const requestedQuantity = Math.floor(Number(this.mobileQuantity.value || 1));
    const quantity = Number.isFinite(requestedQuantity) ? Math.max(1, requestedQuantity) : 1;

    if (!variantId || !quantityInput) {
      this.mobileSelect.focus();
      return;
    }

    quantityInput.value = quantity;
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.mobileQuantity.value = '1';
  }

  removeMobileSelection(event) {
    const removeButton = event.target.closest?.('[data-bbp-mobile-remove]');
    if (!removeButton) return;

    const quantityInput = this.findQuantityInput(removeButton.dataset.variantId);
    if (!quantityInput) return;

    quantityInput.value = 0;
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  renderMobileSummary() {
    if (!this.mobileList || !this.mobileEmpty) return;

    const selectedInputs = this.quantityInputs.filter((input) => Number(input.value || 0) > 0);
    this.mobileList.replaceChildren();
    this.mobileEmpty.hidden = selectedInputs.length > 0;

    selectedInputs.forEach((input) => {
      const quantity = Math.max(0, Math.floor(Number(input.value || 0)));
      const item = document.createElement('div');
      const details = document.createElement('div');
      const name = document.createElement('div');
      const meta = document.createElement('div');
      const remove = document.createElement('button');

      item.className = 'bbp__mobile-item';
      name.className = 'bbp__mobile-item-name';
      meta.className = 'bbp__mobile-item-meta';
      remove.className = 'bbp__mobile-remove';
      remove.type = 'button';
      remove.dataset.bbpMobileRemove = '';
      remove.dataset.variantId = input.dataset.variantId;

      name.textContent = input.dataset.productLabel || 'Butcher Block';
      meta.textContent = input.dataset.productPrice
        ? `${quantity} pcs x ${input.dataset.productPrice}`
        : `${quantity} pcs`;
      remove.textContent = this.mobileList.dataset.removeLabel || 'Remove';

      details.append(name, meta);
      item.append(details, remove);
      this.mobileList.append(item);
    });
  }

  setStatus(message, state) {
    if (!this.status || !message) return;

    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  async addSelectedItems() {
    const items = this.getSelectedItems();
    const totalQuantity = this.getTotalQuantity();

    if (totalQuantity < this.minimumQuantity) {
      this.updateState();
      return;
    }

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');
    this.setStatus(this.dataset.addingMessage, 'loading');

    try {
      let addUrl = window.routes?.cart_add_url || '/cart/add';
      if (!addUrl.endsWith('.js')) addUrl = `${addUrl}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorMessage);
      }

      window.location.href = window.routes?.cart_url || '/cart';
    } catch (error) {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('loading');
      this.setStatus(error.message || this.dataset.errorMessage, 'error');
    }
  }
}

if (!customElements.get('butcher-block-purchase')) {
  customElements.define('butcher-block-purchase', ButcherBlockPurchase);
}
